import { isMobile } from "react-device-detect";
import { getSourceFromPath, isFreshSignedUrl, isHttpUrl, isSignableStoragePath } from "./playerUtils";

export const getVideoHeightFromKey = (key: any) => {
	const text = String(key || "").toLowerCase();
	const match = text.match(/vid_h264_(\d{3,4})/) || text.match(/(\d{3,4})p/);

	if (!match?.[1]) {
		return -1;
	}

	const height = Number(match[1]);

	return Number.isFinite(height) ? height : -1;
};

export const listCoconutRenditions = (coconutJob: any) => {
	return (Object.entries(coconutJob?.renditions || {}) as [string, any][]).filter(([key]) => !String(key).includes("_signedUrl")).map(([key, rendition]) => ({ key, ...(rendition || {}) }));
};

export const listImageRenditions = (imageJob: any) => {
	return (Object.entries(imageJob?.renditions || {}) as [string, any][]).filter(([key]) => !String(key).includes("_signedUrl")).map(([key, rendition]) => ({ key, ...(rendition || {}) }));
};

export const getPreviewAssetId = (previewAsset: any) => {
	return previewAsset?.id || previewAsset?.preview_asset_id || previewAsset?.fio_asset_id || previewAsset?.upload_id || previewAsset?.uploadId || previewAsset?.coconut_upload_id || previewAsset?.image_upload_id || "";
};

export const getPreviewAssetUploadId = (previewAsset: any) => {
	return previewAsset?.upload_id || previewAsset?.uploadId || previewAsset?.coconut_upload_id || previewAsset?.image_upload_id || previewAsset?.id || "";
};

export const getPreviewAssetSelectionId = (previewAsset: any) => {
	if (!previewAsset) {
		return "";
	}

	if (previewAsset?.selection_id) {
		return previewAsset.selection_id;
	}

	if (previewAsset?.provider === "coconut" || previewAsset?.provider === "image") {
		return getPreviewAssetUploadId(previewAsset) || getPreviewAssetId(previewAsset);
	}

	return previewAsset?.fio_asset_id || getPreviewAssetId(previewAsset);
};

const getStableSourceFileName = (value: any, fallback: string = "") => {
	return value?.sourceFileName || value?.source_file_name || value?.originalFileName || value?.original_file_name || value?.fileName || value?.file_name || fallback || "";
};

const getCoconutFileName = (job: any, uploadId: string) => {
	return job?.fileName || job?.file_name || job?.sourceFileName || job?.source_file_name || job?.announcedFile?.fileName || uploadId;
};

const getCoconutFileType = (job: any) => {
	return job?.fileType || job?.file_type || job?.sourceFileType || job?.source_file_type || job?.asset_info?.file_type || "";
};

const getCoconutFileSize = (job: any) => {
	return job?.fileSize || job?.file_size || job?.sourceFileSize || job?.source_file_size || 0;
};

const getImageFileName = (job: any, uploadId: string) => {
	return job?.fileName || job?.file_name || job?.sourceFileName || job?.source_file_name || uploadId;
};

const getImageFileType = (job: any) => {
	return job?.fileType || job?.file_type || job?.sourceFileType || job?.source_file_type || job?.outputFormat?.contentType || job?.asset_info?.file_type || "";
};

const getImageFileSize = (job: any) => {
	return job?.fileSize || job?.file_size || job?.sourceFileSize || job?.source_file_size || job?.asset_info?.file_size || 0;
};

export const getPreviewAssets = (versionValues: any) => {
	const fioAssets = (Object.entries(versionValues?.fio_asset_ids || {}) as [string, any][]).map(([fioAssetKey, fioAsset], sourceIndex) => {
		const fioAssetId = fioAsset?.fio_asset_id || fioAssetKey;
		const sourceFileName = getStableSourceFileName(fioAsset, fioAssetId || "Frame.io asset");

		return {
			...fioAsset,
			provider: "fio",
			id: fioAssetId,
			selection_id: fioAssetId,
			preview_asset_id: fioAssetId,
			fio_asset_id: fioAssetId,
			file_name: fioAsset?.file_name || sourceFileName || fioAssetId || "Frame.io asset",
			file_type: fioAsset?.file_type || "",
			file_size: fioAsset?.file_size || fioAsset?.asset_info?.file_size || 0,
			source_file_name: sourceFileName,
			sort_file_name: sourceFileName,
			source_order_index: typeof fioAsset?.source_order_index === "number" ? fioAsset.source_order_index : sourceIndex,
			status: fioAsset?.status || "",
		};
	});

	const coconutAssets = (Object.entries(versionValues?.coconut_jobs || {}) as [string, any][]).map(([uploadId, job], sourceIndex) => {
		const sourceFileName = getStableSourceFileName(job, uploadId);

		return {
			...(job || {}),
			provider: "coconut",
			id: uploadId,
			selection_id: uploadId,
			preview_asset_id: uploadId,
			upload_id: uploadId,
			uploadId,
			coconut_upload_id: uploadId,
			fio_asset_id: uploadId,
			file_name: getCoconutFileName(job, uploadId),
			file_type: getCoconutFileType(job),
			file_size: getCoconutFileSize(job),
			source_file_name: sourceFileName,
			sort_file_name: sourceFileName,
			source_order_index: typeof job?.source_order_index === "number" ? job.source_order_index : sourceIndex,
			status: job?.status || "",
			asset_info: job?.asset_info || null,
		};
	});

	const imageAssets = (Object.entries(versionValues?.image_jobs || {}) as [string, any][]).map(([uploadId, job], sourceIndex) => {
		const sourceFileName = getStableSourceFileName(job, uploadId);

		return {
			...(job || {}),
			provider: "image",
			id: uploadId,
			selection_id: uploadId,
			preview_asset_id: uploadId,
			upload_id: uploadId,
			uploadId,
			image_upload_id: uploadId,
			fio_asset_id: uploadId,
			file_name: getImageFileName(job, uploadId),
			file_type: getImageFileType(job),
			file_size: getImageFileSize(job),
			source_file_name: sourceFileName,
			sort_file_name: sourceFileName,
			source_order_index: typeof job?.source_order_index === "number" ? job.source_order_index : sourceIndex,
			status: job?.status || "",
			asset_info: { ...(job?.asset_info || {}), file_type: getImageFileType(job), file_size: getImageFileSize(job), is_image: true, is_video: false },
		};
	});

	return [...fioAssets, ...coconutAssets, ...imageAssets].filter((item: any) => Boolean(getPreviewAssetId(item)));
};

export const getPreviewAssetSortFileName = (previewAsset: any) => {
	return String(
		previewAsset?.sort_file_name ||
			previewAsset?.source_file_name ||
			previewAsset?.sourceFileName ||
			previewAsset?.original_file_name ||
			previewAsset?.originalFileName ||
			previewAsset?.file_name ||
			previewAsset?.fileName ||
			previewAsset?.sourcePath ||
			previewAsset?.filePath ||
			getPreviewAssetId(previewAsset) ||
			""
	);
};

const compareTextStable = (a: string, b: string) => {
	return String(a || "").localeCompare(String(b || ""), undefined, { numeric: true, sensitivity: "base" });
};

const normalizeCompareText = (value: any) => {
	return String(value || "")
		.trim()
		.toLowerCase();
};

const getAnnouncedFileSortValue = (announcedFile: any, fallbackIndex: number) => {
	const explicitOrder = announcedFile?.sortIndex ?? announcedFile?.sort_index ?? announcedFile?.order ?? announcedFile?.index;
	const parsed = Number(explicitOrder);

	if (Number.isFinite(parsed)) {
		return parsed;
	}

	return fallbackIndex;
};

const getAnnouncedFileOrderMaps = (versionValues: any) => {
	const announcedEntries = Object.entries(versionValues?.announced_files || {});
	const byKey: Record<string, number> = {};
	const byFileName: Record<string, number> = {};
	const byFilePath: Record<string, number> = {};

	announcedEntries.forEach(([key, announcedFile]: any, index: number) => {
		const sortValue = getAnnouncedFileSortValue(announcedFile, index);
		const fileName = String(announcedFile?.fileName || announcedFile?.file_name || announcedFile?.name || "").toLowerCase();
		const filePath = String(announcedFile?.filePath || announcedFile?.file_path || announcedFile?.expectedPath || announcedFile?.expected_path || "").toLowerCase();

		byKey[String(key)] = sortValue;

		if (fileName) {
			byFileName[fileName] = sortValue;
		}

		if (filePath) {
			byFilePath[filePath] = sortValue;
		}
	});

	return { byKey, byFileName, byFilePath };
};

const getPreviewAssetAnnouncedIndex = (previewAsset: any, announcedOrderMaps: any) => {
	const announcedFileKey = String(previewAsset?.announcedFileKey || previewAsset?.announced_file_key || "");
	const fileName = String(previewAsset?.fileName || previewAsset?.file_name || previewAsset?.sourceFileName || previewAsset?.source_file_name || "").toLowerCase();
	const filePath = String(previewAsset?.filePath || previewAsset?.sourcePath || previewAsset?.source_path || "").toLowerCase();

	if (announcedFileKey && Number.isFinite(announcedOrderMaps.byKey?.[announcedFileKey])) {
		return announcedOrderMaps.byKey[announcedFileKey];
	}

	if (filePath && Number.isFinite(announcedOrderMaps.byFilePath?.[filePath])) {
		return announcedOrderMaps.byFilePath[filePath];
	}

	if (fileName && Number.isFinite(announcedOrderMaps.byFileName?.[fileName])) {
		return announcedOrderMaps.byFileName[fileName];
	}

	return Number.MAX_SAFE_INTEGER;
};

export const sortPreviewAssetsBySourceFileName = (previewAssets: any[], versionValues: any = {}) => {
	const announcedOrderMaps = getAnnouncedFileOrderMaps(versionValues);

	return [...(previewAssets || [])]
		.map((previewAsset: any, originalIndex: number) => ({ previewAsset, originalIndex, announcedIndex: getPreviewAssetAnnouncedIndex(previewAsset, announcedOrderMaps) }))
		.sort((a: any, b: any) => {
			if (a.announcedIndex !== b.announcedIndex) {
				return a.announcedIndex - b.announcedIndex;
			}

			const nameCompare = compareTextStable(getPreviewAssetSortFileName(a.previewAsset), getPreviewAssetSortFileName(b.previewAsset));

			if (nameCompare !== 0) {
				return nameCompare;
			}

			const providerCompare = compareTextStable(a.previewAsset?.provider, b.previewAsset?.provider);

			if (providerCompare !== 0) {
				return providerCompare;
			}

			const idCompare = compareTextStable(getPreviewAssetId(a.previewAsset), getPreviewAssetId(b.previewAsset));

			if (idCompare !== 0) {
				return idCompare;
			}

			const aSourceIndex = a.previewAsset?.source_order_index;
			const bSourceIndex = b.previewAsset?.source_order_index;

			if (typeof aSourceIndex === "number" || typeof bSourceIndex === "number") {
				if (typeof aSourceIndex !== "number") return 1;
				if (typeof bSourceIndex !== "number") return -1;

				if (aSourceIndex !== bSourceIndex) {
					return aSourceIndex - bSourceIndex;
				}
			}

			return a.originalIndex - b.originalIndex;
		})
		.map(({ previewAsset }: any) => previewAsset);
};

export const getDefaultPreviewAssetId = (versionValues: any) => {
	const favoriteId = versionValues?.cur_preview_fav;

	if (favoriteId && versionValues?.fio_asset_ids?.[favoriteId]) {
		return favoriteId;
	}

	if (favoriteId && versionValues?.coconut_jobs?.[favoriteId]) {
		return favoriteId;
	}

	if (favoriteId && versionValues?.image_jobs?.[favoriteId]) {
		return favoriteId;
	}

	const previewAssets = sortPreviewAssetsBySourceFileName(getPreviewAssets(versionValues), versionValues);

	return getPreviewAssetId(previewAssets?.[0]) || "";
};

export const getMatchingPreviewAssetId = ({ primaryVersionValues, compareVersionValues, primaryPreviewAssetId }: { primaryVersionValues: any; compareVersionValues: any; primaryPreviewAssetId: string }) => {
	const primaryPreviewAssets = sortPreviewAssetsBySourceFileName(getPreviewAssets(primaryVersionValues), primaryVersionValues);
	const comparePreviewAssets = sortPreviewAssetsBySourceFileName(getPreviewAssets(compareVersionValues), compareVersionValues);

	if (!comparePreviewAssets.length) {
		return "";
	}

	const sameIdMatch = comparePreviewAssets.find((previewAsset: any) => getPreviewAssetId(previewAsset) === primaryPreviewAssetId);

	if (sameIdMatch) {
		return getPreviewAssetId(sameIdMatch);
	}

	const primaryIndex = primaryPreviewAssets.findIndex((previewAsset: any) => getPreviewAssetId(previewAsset) === primaryPreviewAssetId);
	const primaryPreviewAsset = primaryIndex > -1 ? primaryPreviewAssets[primaryIndex] : null;

	if (!primaryPreviewAsset) {
		return getPreviewAssetId(comparePreviewAssets[0]) || "";
	}

	const primarySourceFileName = normalizeCompareText(primaryPreviewAsset?.source_file_name || primaryPreviewAsset?.sourceFileName);
	const primarySortFileName = normalizeCompareText(primaryPreviewAsset?.sort_file_name || primaryPreviewAsset?.sortFileName);
	const primaryFileName = normalizeCompareText(primaryPreviewAsset?.file_name || primaryPreviewAsset?.fileName);
	const primaryOrderIndex = primaryPreviewAsset?.source_order_index;

	const sourceFileNameMatch = primarySourceFileName
		? comparePreviewAssets.find((previewAsset: any) => {
				return normalizeCompareText(previewAsset?.source_file_name || previewAsset?.sourceFileName) === primarySourceFileName;
		  })
		: null;

	if (sourceFileNameMatch) {
		return getPreviewAssetId(sourceFileNameMatch);
	}

	const sortFileNameMatch = primarySortFileName
		? comparePreviewAssets.find((previewAsset: any) => {
				return normalizeCompareText(previewAsset?.sort_file_name || previewAsset?.sortFileName) === primarySortFileName;
		  })
		: null;

	if (sortFileNameMatch) {
		return getPreviewAssetId(sortFileNameMatch);
	}

	const fileNameMatch = primaryFileName
		? comparePreviewAssets.find((previewAsset: any) => {
				return normalizeCompareText(previewAsset?.file_name || previewAsset?.fileName) === primaryFileName;
		  })
		: null;

	if (fileNameMatch) {
		return getPreviewAssetId(fileNameMatch);
	}

	if (typeof primaryOrderIndex === "number") {
		const sourceOrderMatch = comparePreviewAssets.find((previewAsset: any) => previewAsset?.source_order_index === primaryOrderIndex);

		if (sourceOrderMatch) {
			return getPreviewAssetId(sourceOrderMatch);
		}
	}

	if (primaryIndex > -1 && comparePreviewAssets[primaryIndex]) {
		return getPreviewAssetId(comparePreviewAssets[primaryIndex]);
	}

	return getPreviewAssetId(comparePreviewAssets[0]) || "";
};

export const getBestCoconutSourceForKey = (versionValues: any, sourceId: string, key: string) => {
	const candidates = [
		`coconut_jobs.${sourceId}.preview.${key}`,
		`coconut_jobs.${sourceId}.renditions.${key}.path`,
		`coconut_jobs.${sourceId}.renditions.${key}.previewPath`,
		`coconut_jobs.${sourceId}.renditions.${key}.destinationPath`,
		`coconut_jobs.${sourceId}.downloadedOutputs.${key}.path`,
		`coconut_jobs.${sourceId}.downloadedOutputs.${key}.previewPath`,
		`coconut_jobs.${sourceId}.downloadedOutputs.${key}.destinationPath`,
		`coconut_jobs.${sourceId}.transcodes.${key}.path`,
		`coconut_jobs.${sourceId}.transcodes.${key}.previewPath`,
		`coconut_jobs.${sourceId}.transcodes.${key}.destinationPath`,
	];

	for (const pathField of candidates) {
		const source = getSourceFromPath(versionValues, pathField);

		if (source && (isSignableStoragePath(source.path) || isHttpUrl(source.path) || isFreshSignedUrl(source.signedUrl))) {
			return source;
		}
	}

	return null;
};

export const getBestImageSourceForKey = (versionValues: any, sourceId: string, key: string) => {
	const candidates = [
		`image_jobs.${sourceId}.preview.${key}`,
		`image_jobs.${sourceId}.renditions.${key}.path`,
		`image_jobs.${sourceId}.renditions.${key}.previewPath`,
		`image_jobs.${sourceId}.renditions.${key}.destinationPath`,
		`image_jobs.${sourceId}.renditions.${key}.storagePath`,
		`image_jobs.${sourceId}.generatedOutputs.${key}.path`,
		`image_jobs.${sourceId}.generatedOutputs.${key}.previewPath`,
		`image_jobs.${sourceId}.generatedOutputs.${key}.destinationPath`,
		`image_jobs.${sourceId}.generatedOutputs.${key}.storagePath`,
	];

	for (const pathField of candidates) {
		const source = getSourceFromPath(versionValues, pathField);

		if (source && (isSignableStoragePath(source.path) || isHttpUrl(source.path) || isFreshSignedUrl(source.signedUrl))) {
			return source;
		}
	}

	return null;
};

const uniqueStrings = (values: string[]) => {
	return values.filter(Boolean).filter((value, index, arr) => arr.indexOf(value) === index);
};

const getImageRenditionText = (rendition: any) => {
	return [rendition?.role, rendition?.previewKey, rendition?.key, rendition?.label, rendition?.group, rendition?.mediaType, rendition?.path, rendition?.previewPath, rendition?.destinationPath, rendition?.storagePath]
		.filter(Boolean)
		.join(" ")
		.toLowerCase();
};

const is2160ImageRendition = (rendition: any) => {
	const text = getImageRenditionText(rendition);

	return rendition?.role === "max_2160" || text.includes("max_2160") || text.includes("max2160") || text.includes("2160") || rendition?.previewKey === "img_2160" || rendition?.key === "img_2160";
};

const isFullImageRendition = (rendition: any) => {
	const text = getImageRenditionText(rendition);

	if (is2160ImageRendition(rendition)) {
		return false;
	}

	return rendition?.role === "full" || text.includes("img_full") || text.includes(" full ") || text.endsWith(" full") || text.startsWith("full ");
};

const isCoverImageRendition = (rendition: any) => {
	const text = getImageRenditionText(rendition);

	return rendition?.role === "cover" || text.includes("cover") || rendition?.previewKey === "img_cover" || rendition?.key === "img_cover";
};

const isThumbnailImageRendition = (rendition: any) => {
	const text = getImageRenditionText(rendition);

	return rendition?.role === "thumbnail" || text.includes("thumbnail") || text.includes("thumb") || rendition?.previewKey === "img_thumb" || rendition?.key === "img_thumb";
};

const isGenericImageRendition = (rendition: any) => {
	const text = getImageRenditionText(rendition);

	if (is2160ImageRendition(rendition) || isFullImageRendition(rendition) || isCoverImageRendition(rendition) || isThumbnailImageRendition(rendition)) {
		return false;
	}

	return rendition?.role === "image" || rendition?.group === "image" || rendition?.mediaType === "image" || text.includes("img_high") || text.includes("img_large") || text.includes("source_proxy");
};

const getImageRenditionKeys = (imageJob: any, predicate: (rendition: any) => boolean, fallbackKeys: string[]) => {
	const keys = listImageRenditions(imageJob)
		.filter(predicate)
		.map((rendition) => rendition?.previewKey || rendition?.key)
		.filter(Boolean);

	return uniqueStrings([...keys, ...fallbackKeys]);
};

const getFirstImageSourceForKeys = (versionValues: any, sourceId: string, keys: string[]) => {
	for (const key of keys) {
		const source = getBestImageSourceForKey(versionValues, sourceId, key);

		if (source) {
			return source;
		}
	}

	return null;
};

export const getPreferredImageSource = (assetInfo: any) => {
	return assetInfo?.image2160Source || assetInfo?.max2160Source || assetInfo?.img2160Source || assetInfo?.fullSource || assetInfo?.genericImageSource || assetInfo?.imageSource || assetInfo?.coverSource || assetInfo?.thumbnailSource || null;
};

export const getPreviewSourceContext = (versionValues: any, sourceId: any): any => {
	if (sourceId && versionValues?.fio_asset_ids?.[sourceId]) {
		const fioAsset = versionValues.fio_asset_ids[sourceId];
		const assetInfo = fioAsset?.asset_info || null;
		const hasVideo =
			(assetInfo?.fps || 0) > 0 ||
			Object.keys(fioAsset || {}).some((key) => {
				return key.startsWith("vid") && !key.endsWith("signedUrl") && fioAsset?.[key] && fioAsset?.[key] !== "error";
			});

		return {
			provider: "fio",
			id: sourceId,
			selection_id: sourceId,
			fio_asset_id: sourceId,
			data: fioAsset,
			fileName: fioAsset?.file_name || sourceId,
			assetInfo,
			isVideo: hasVideo,
			isFrameAccurate: Boolean((assetInfo?.fps || 0) > 0 && (assetInfo?.frames || 0) > 0),
		};
	}

	if (sourceId && versionValues?.coconut_jobs?.[sourceId]) {
		const coconutJob = versionValues.coconut_jobs[sourceId];
		const assetInfo = coconutJob?.asset_info || null;
		const hasVideo =
			(assetInfo?.fps || 0) > 0 ||
			listCoconutRenditions(coconutJob).some((rendition) => {
				return rendition?.mediaType === "video" || rendition?.group === "video" || String(rendition?.previewKey || rendition?.key || "").startsWith("vid_");
			}) ||
			Object.keys(coconutJob?.preview || {}).some((key) => key.startsWith("vid_"));

		return {
			provider: "coconut",
			id: sourceId,
			selection_id: sourceId,
			upload_id: sourceId,
			uploadId: sourceId,
			coconut_upload_id: sourceId,
			data: coconutJob,
			fileName: coconutJob?.fileName || coconutJob?.file_name || coconutJob?.sourceFileName || coconutJob?.source_file_name || sourceId,
			assetInfo,
			isVideo: hasVideo,
			isFrameAccurate: Boolean((assetInfo?.fps || 0) > 0 && (assetInfo?.frames || 0) > 0),
		};
	}

	if (sourceId && versionValues?.image_jobs?.[sourceId]) {
		const imageJob = versionValues.image_jobs[sourceId];

		const image2160Keys = getImageRenditionKeys(imageJob, is2160ImageRendition, ["img_2160", "image_2160", "max_2160"]);
		const fullKeys = getImageRenditionKeys(imageJob, isFullImageRendition, ["img_full", "full"]);
		const genericImageKeys = getImageRenditionKeys(imageJob, isGenericImageRendition, ["img_high", "img_large", "image"]);
		const coverKeys = getImageRenditionKeys(imageJob, isCoverImageRendition, ["img_cover"]);
		const thumbnailKeys = getImageRenditionKeys(imageJob, isThumbnailImageRendition, ["img_thumb"]);

		const image2160Source = getFirstImageSourceForKeys(versionValues, sourceId, image2160Keys);
		const fullSource = getFirstImageSourceForKeys(versionValues, sourceId, fullKeys);
		const genericImageSource = getFirstImageSourceForKeys(versionValues, sourceId, genericImageKeys);
		const coverSource = getFirstImageSourceForKeys(versionValues, sourceId, coverKeys);
		const thumbnailSource = getFirstImageSourceForKeys(versionValues, sourceId, thumbnailKeys);

		const imageSource = image2160Source || fullSource || genericImageSource || coverSource || thumbnailSource;

		const fileType = getImageFileType(imageJob);
		const fileSize = getImageFileSize(imageJob);

		const assetInfo = {
			...(imageJob?.asset_info || {}),
			provider: "image",
			id: sourceId,
			selection_id: sourceId,
			upload_id: sourceId,
			uploadId: sourceId,
			image_upload_id: sourceId,
			file_name: getImageFileName(imageJob, sourceId),
			file_type: fileType,
			file_size: fileSize,
			is_image: true,
			is_video: false,

			image2160Source,
			max2160Source: image2160Source,
			img2160Source: image2160Source,
			fullSource,
			genericImageSource,
			coverSource,
			thumbnailSource,

			// Main display source. Priority: 2160 > full > generic image > cover > thumbnail.
			imageSource,
		};

		return {
			provider: "image",
			id: sourceId,
			selection_id: sourceId,
			upload_id: sourceId,
			uploadId: sourceId,
			image_upload_id: sourceId,
			data: imageJob,
			fileName: getImageFileName(imageJob, sourceId),
			assetInfo,
			isVideo: false,
			isFrameAccurate: false,
		};
	}

	return { provider: "none", id: "", selection_id: "", data: null, fileName: "", assetInfo: null, isVideo: false, isFrameAccurate: false };
};

export const getAvailableVideoSources = (versionValues: any, sourceContext: any) => {
	if (sourceContext.provider === "fio") {
		return Object.keys(sourceContext.data || {})
			.filter((key: any) => {
				return key.startsWith("vid") && !key.includes("signedUrl") && sourceContext.data?.[key] !== "error" && sourceContext.data?.[key] !== "" && typeof sourceContext.data?.[key] !== "undefined";
			})
			.map((key) => {
				const source = getSourceFromPath(versionValues, `fio_asset_ids.${sourceContext.id}.${key}`);
				return { key, height: getVideoHeightFromKey(key), source };
			})
			.filter((item) => item.source && (isSignableStoragePath(item.source.path) || isHttpUrl(item.source.path) || isFreshSignedUrl(item.source.signedUrl)));
	}

	if (sourceContext.provider === "coconut") {
		const renditionSources = listCoconutRenditions(sourceContext.data)
			.filter((rendition) => {
				return rendition?.mediaType === "video" || rendition?.group === "video" || String(rendition?.previewKey || rendition?.key || "").startsWith("vid_");
			})
			.map((rendition) => {
				const key = rendition.previewKey || rendition.key;
				const source = getBestCoconutSourceForKey(versionValues, sourceContext.id, key);
				return { key, height: rendition.height || getVideoHeightFromKey(key), source };
			})
			.filter((item) => item.source && (isSignableStoragePath(item.source.path) || isHttpUrl(item.source.path) || isFreshSignedUrl(item.source.signedUrl)));

		if (renditionSources.length) {
			return renditionSources;
		}

		return Object.keys(sourceContext.data?.preview || {})
			.filter((key) => key.startsWith("vid_") && !key.includes("signedUrl"))
			.map((key) => {
				const source = getBestCoconutSourceForKey(versionValues, sourceContext.id, key);
				return { key, height: getVideoHeightFromKey(key), source };
			})
			.filter((item) => item.source && (isSignableStoragePath(item.source.path) || isHttpUrl(item.source.path) || isFreshSignedUrl(item.source.signedUrl)));
	}

	return [];
};

export const pickBestVideoSource = (sources: any[], desiredVideoQuality: number) => {
	const sortedSources = [...sources].sort((a, b) => {
		return (a.height || -1) - (b.height || -1);
	});

	if (!sortedSources.length) {
		return null;
	}

	let bestSource = sortedSources[sortedSources.length - 1];

	if (desiredVideoQuality > 0) {
		const allQualities = sortedSources.map((item) => item.height).filter((height) => height > 0);

		if (allQualities.length) {
			const closest = allQualities.reduce((prev: number, curr: number) => {
				return Math.abs(curr - desiredVideoQuality) < Math.abs(prev - desiredVideoQuality) ? curr : prev;
			});

			bestSource = sortedSources.find((item) => item.height === closest) || bestSource;
		}
	}

	if (bestSource?.height === 2160 && isMobile && sortedSources.length > 1) {
		return sortedSources[sortedSources.length - 2];
	}

	return bestSource;
};

export const toIdArray = (value: any) => {
	if (Array.isArray(value)) {
		return value
			.map((item: any) => {
				if (typeof item === "string") return item;
				if (item?.selection_id) return item.selection_id;
				if (item?.id) return item.id;
				if (item?.fio_asset_id) return item.fio_asset_id;
				if (item?.upload_id) return item.upload_id;
				if (item?.uploadId) return item.uploadId;
				if (item?.coconut_upload_id) return item.coconut_upload_id;
				if (item?.image_upload_id) return item.image_upload_id;
				return item;
			})
			.filter(Boolean);
	}

	if (value && typeof value === "object") {
		return Object.entries(value)
			.map(([key, val]: any) => {
				if (typeof val === "string") return val;
				if (val === true) return key;
				if (val?.selection_id) return val.selection_id;
				if (val?.id) return val.id;
				if (val?.fio_asset_id) return val.fio_asset_id;
				if (val?.upload_id) return val.upload_id;
				if (val?.uploadId) return val.uploadId;
				if (val?.coconut_upload_id) return val.coconut_upload_id;
				if (val?.image_upload_id) return val.image_upload_id;
				return key;
			})
			.filter(Boolean);
	}

	return [];
};
